import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtCRC } from '../api';
import { IconBank, IconCash, IconChat, IconRefresh, IconUsers } from '../components/Icons';

// `to` convierte la tarjeta en un atajo a la lista ya filtrada
function Stat({ label, value, hint, tone, Icon, to }) {
  const Tag = to ? Link : 'div';
  return (
    <Tag {...(to ? { to } : {})} className={`dashboard-stat ${tone} ${to ? 'is-link' : ''}`}>
      <div className="dashboard-stat-top">
        <span className="dashboard-stat-icon"><Icon size={18} /></span>
        <span className="dashboard-stat-trend">{hint}</span>
      </div>
      <strong>{value}</strong>
      <span>{label}</span>
    </Tag>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api('/dashboard').then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="form-error">{error}</div>;
  if (!data) return <div className="page-loading"><span /> Cargando tu operación…</div>;

  const max = Math.max(...data.incomeByMonth.map((m) => m.total), 1);
  const collectionRate = data.expectedMonthly > 0 ? Math.min(Math.round((data.monthIncome / data.expectedMonthly) * 100), 100) : 0;
  const today = new Date().toLocaleDateString('es-CR', { weekday: 'long', day: 'numeric', month: 'long' });
  const capitalizedDate = today.charAt(0).toUpperCase() + today.slice(1);

  return (
    <div className="dashboard-page">
      <header className="page-hero">
        <div>
          <div className="eyebrow">Resumen del negocio</div>
          <h1>Panel de control</h1>
          <p>{capitalizedDate} · así se mueve tu operación hoy.</p>
        </div>
        <Link to="/suscripciones" className="btn-primary"><IconRefresh size={16} /> Nueva suscripción</Link>
      </header>

      <section className="dashboard-stats-grid">
        <Stat label="Clientes registrados" value={data.totalClients} hint="Base total" tone="tone-blue" Icon={IconUsers} to="/clientes" />
        <Stat label="Suscripciones activas" value={data.activeSubs} hint="Generando ingreso" tone="tone-green" Icon={IconRefresh} to="/suscripciones?status=activa" />
        <Stat label="Vencen en 7 días" value={data.dueSoon} hint="Próximas" tone="tone-amber" Icon={IconCash} to="/suscripciones?due=por_vencer&days=7" />
        <Stat label="Suscripciones vencidas" value={data.expiredSubs} hint="Por recuperar" tone="tone-red" Icon={IconChat} to="/suscripciones?due=vencidas" />
      </section>

      <section className="dashboard-main-grid">
        <article className="revenue-card">
          <div className="revenue-card-head">
            <div>
              <span>Ingresos cobrados este mes</span>
              <strong>{fmtCRC(data.monthIncome)}</strong>
            </div>
            <span className="revenue-count">{data.monthPayments} {data.monthPayments === 1 ? 'pago' : 'pagos'}</span>
          </div>

          <div className="revenue-progress-copy">
            <span>Avance sobre el ingreso esperado</span>
            <strong>{collectionRate}%</strong>
          </div>
          <div className="revenue-progress"><i style={{ width: `${collectionRate}%` }} /></div>

          <div className="revenue-footer">
            <div><span>Ingreso mensual esperado</span><strong>{fmtCRC(data.expectedMonthly)}</strong></div>
            <Link to="/suscripciones">Ver suscripciones →</Link>
          </div>
        </article>

        <article className="income-chart-card">
          <div className="chart-head">
            <div>
              <span className="eyebrow">Tendencia</span>
              <h2>Ingresos de los últimos 6 meses</h2>
            </div>
            <span className="chart-legend"><i /> Cobrado</span>
          </div>

          {data.incomeByMonth.length === 0 ? (
            <div className="chart-empty">
              <span><IconBank size={22} /></span>
              <strong>Aún no hay pagos registrados</strong>
              <p>Cuando renueves una suscripción verás aquí la evolución del negocio.</p>
            </div>
          ) : (
            <div className="income-chart">
              <div className="chart-grid-lines"><i /><i /><i /></div>
              {data.incomeByMonth.map((m) => (
                <div key={m.month} className="chart-column">
                  <div className="chart-value">{fmtCRC(m.total)}</div>
                  <div className="chart-bar-track">
                    <i style={{ height: `${Math.max((m.total / max) * 100, 6)}%` }} title={fmtCRC(m.total)} />
                  </div>
                  <span>{new Date(`${m.month}-02`).toLocaleDateString('es-CR', { month: 'short' })}</span>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>

      {(data.dueSoon > 0 || data.expiredSubs > 0) ? (
        <section className="attention-strip">
          <div className="attention-icon"><IconChat size={20} /></div>
          <div>
            <strong>Hay {data.dueSoon + data.expiredSubs} cobros que necesitan atención</strong>
            <span>Abre WhatsApp con el mensaje listo y mantén tus renovaciones al día.</span>
          </div>
          <Link to="/recordatorios">Revisar cobros <span>→</span></Link>
        </section>
      ) : (
        <section className="attention-strip is-clear">
          <div className="attention-icon"><IconCash size={20} /></div>
          <div><strong>Todo está al día</strong><span>No hay cobros vencidos ni próximos esta semana.</span></div>
        </section>
      )}
    </div>
  );
}
